import React, { useState, useCallback, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import {
  ConfirmDialog,
  LayerContextProvider,
  layers,
  Caption1,
  Body2,
  Label,
  Header3,
  Dialog,
  Button,
  Input,
  Tooltip,
  InfoIcon,
} from "@activecollab/components";
import { Formik, Form, Field, FieldArray } from "formik";
import { List } from "immutable";
import { capitalize } from "lodash";
import PropTypes from "prop-types";
import { useFocusInvalidField } from "~/components/Hooks/useFocusInvalidField.js";
import { useGlobalPrivateChannelListener } from "~/components/Hooks/useGlobalPrivateChannelListener.js";
import InsertTextVariables from "~/components/InsertTextVariables/InsertTextVariables.js";
import { useRecurringTaskContext } from "~/components/Providers/RecurringTask/context";
import AddSubtaskTriggerContainer from "~/components/Subtasks/AddSubtaskTriggerContainer";
import SubtaskRecurringTaskForm from "~/components/Subtasks/SubtaskRecurringTaskForm";
import RecurringSubtaskList from "~/components/tasks/CreateRecurringTaskModal/RecurringSubtaskList";
import StartOnDueOn from "~/components/tasks/CreateRecurringTaskModal/StartOnDueOn";
import { showErrorToast, showSuccessToast } from "~/components/Toast/index.js";
import { RecurrenceSelect } from "~/pages/ProjectTemplates/RecurringTasks/RecurrenceSelect.jsx";
import LabelMap from "~/records/record.label_map.jsx";
import TaskListMap from "~/records/record.task_list_map.jsx";
import { addRecurringTask, editRecurringTask } from "~/store/actions/actions.recurringTasks.jsx";
import { keyCodes } from "~/utils/key_codes.jsx";
import { recurringTaskValidator } from "~/validators/validate.recurringTask.js";
import { SelectTaskLabels } from "./../SelectTaskLabels.js";
import { SubscribersPicker } from "./../TaskPanel/subscribers/SubscribersPicker.js";
import AngDropFiles from "../../component.ang_drop_files.jsx";
import SelectTimeEstimate from "../../component.select_time_estimate.jsx";
import { CustomFormField } from "../../CustomFormField.jsx";
import { useFileHandler } from "../../Hooks/useFileHandler.jsx";
import { FormCheckbox } from "../../ModalObjects";
import { AttachmentUpload } from "../../select_attachments/AttachmentUpload.jsx";
import { SelectAttachments } from "../../select_attachments/SelectAttachments.jsx";
import SelectContainer from "../../SelectContainer/SelectContainer.js";
import SelectTaskList from "../../SelectTaskList.js";
import { SubscribersAvatarList } from "../../SubscribersAvatarList/SubscribersAvatarList.js";
import VisualEditorContainer from "../../VisualEditor/VisualEditorContainer.jsx";

const RecurringTaskForm = ({
  open,
  id,
  projectId,
  users,
  attachments,
  taskLists,
  canSeeTimeEstimates,
  name,
  repeatFrequency,
  repeatAmount,
  repeatAmountExtended,
  description,
  bodyMode,
  taskListId,
  assigneeId,
  startOn,
  dueOn,
  hiddenFromClients,
  important,
  estimate,
  jobTypeId,
  subscribers,
  attachUploadedFiles,
  dropAttachedFiles,
  labels,
  labelNames: defaultLabelNames,
  labelIds: defaultLabelIds,
  onClose,
  hideFromClients,
  isLoggedUserClient,
  subtasks,
  isBillable,
  isTrackingEnabled,
  isProjectNonBillable,
  projectMembers,
}) => {
  const dispatch = useDispatch();
  const inputRef = useRef(null);
  const formRef = useRef(null);
  const submitButtonRef = useRef(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [addNewSubtaskVisible, setAddNewSubtaskVisible] = useState(false);
  const [updatedSubtasksFromRealTime, setUpdatedSubtasksFromRealTime] = useState([]);
  const [variableMenuOpen, setVariableMenuOpen] = useState(false);
  const [inputCursorPosition, setInputCursorPosition] = useState(name.length);
  const { setRecurringTaskCreated } = useRecurringTaskContext();
  const { fieldRef, focusFirstInvalidField } = useFocusInvalidField();

  const possibleSubscribers = useMemo(() => projectMembers.map((m) => m.id), [projectMembers]);

  const showAddSubtaskForm = () => {
    setAddNewSubtaskVisible(true);
  };

  const [members, setMembers] = useState(() =>
    hiddenFromClients ? users.filter((user) => user.class !== "Client") : users
  );

  const clientSubscribersIds = useMemo(() => {
    return projectMembers.filter((u) => u.class === "Client").map((u) => u.id);
  }, [projectMembers]);

  const resolveSubscribers = useCallback(
    (value, subscribers) => {
      if (value) {
        return subscribers?.filter((s) => !clientSubscribersIds.includes(s));
      }
      return subscribers;
    },
    [clientSubscribersIds]
  );

  const resolveSubtaskAssignee = useCallback(
    (value, subtasks) => {
      if (value) {
        return subtasks?.map((s) => {
          if (clientSubscribersIds.includes(s.assignee_id)) {
            return { ...s, assignee_id: null };
          } else return s;
        });
      }
      return subtasks;
    },
    [clientSubscribersIds]
  );

  const resolveRecurringTaskAssignee = useCallback(
    (value, assigneeId) => {
      if (value && clientSubscribersIds.includes(assigneeId)) {
        return null;
      }
      return assigneeId;
    },
    [clientSubscribersIds]
  );

  const { t } = useTranslation();

  const fileInputRef = useRef(null);

  const handleOpenFileWindow = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const { uploading, onUpload, ...fileHandlerRest } = useFileHandler();

  const labelIds = useMemo(() => {
    return defaultLabelNames?.size
      ? labels
          .toList()
          .filter((l) => defaultLabelNames.includes(l.name))
          .map((l) => l.id)
      : defaultLabelIds;
  }, [defaultLabelIds, defaultLabelNames, labels]);

  const labelNames = useMemo(() => {
    return defaultLabelNames && defaultLabelNames.size
      ? defaultLabelNames
      : labels
          .toList()
          .filter((l) => labelIds.includes(l.id))
          .map((l) => l.name);
  }, [defaultLabelNames, labelIds, labels]);

  const handleSubmit = useCallback(
    (values) => {
      if (!formRef?.current?.dirty) {
        return onClose();
      }
      const {
        assigneeId,
        attachments,
        description,
        hiddenFromClients,
        repeat_frequency,
        repeat_amount,
        repeat_amount_extended,
        important,
        name,
        task_list_id,
        timeEstimate,
        subscribers,
        labels,
        subtasks,
        dates,
        isBillable,
      } = values;

      const clientAssignee = users.find((user) => user.id === assigneeId && user.class === "Client");

      const attributes = {
        assignee_id: hiddenFromClients && clientAssignee ? undefined : assigneeId,
        body: description,
        is_hidden_from_clients: !!hiddenFromClients,
        is_important: !!important,
        name,
        repeat_frequency,
        repeat_amount: repeat_frequency === "daily" ? (repeat_amount ? 1 : 0) : repeat_amount,
        repeat_amount_extended,
        reset_attachments: true,
        reset_subscribers: true,
        due_in: dates.due_in === "" ? null : dates.due_in,
        start_in: dates.start_in === "" ? null : dates.start_in,
        subscribers,
        subtasks: subtasks.map((s) => {
          const { id, ...rest } = s;
          return { ...rest };
        }),
        task_list_id,
        job_type_id: timeEstimate.job_type_id,
        estimate: timeEstimate.estimate,
        is_billable: isBillable,
      };

      if (attachments.filesToAttach) {
        attributes.attach_uploaded_files = attachments.filesToAttach;
      }

      if (attachments.filesToDrop) {
        attributes.drop_attached_files = attachments.filesToDrop;
      }

      if (labels.labelNames.size > 0) {
        attributes.labels = labels.labelNames.toArray();
      }

      if (labels.labelNames.size === 0 && labels.labelIds.size > 0) {
        attributes.labels = labels.labelIds.toArray();
      }

      if (!id) {
        dispatch(addRecurringTask(projectId, attributes)).then((action) => {
          const { response = {} } = action;
          const { result = {} } = response;
          const { single: recurringTaskId } = result;

          if (recurringTaskId) {
            setRecurringTaskCreated(true);
            showSuccessToast(t("Recurring task created"));
            onClose();
          } else {
            showErrorToast(t("Failed to create recurring task"));
          }
        });
      } else {
        dispatch(editRecurringTask(projectId, id, attributes)).then((action) => {
          const { response = {} } = action;
          const { result = {} } = response;
          const { single: recurringTaskId } = result;

          if (recurringTaskId) {
            showSuccessToast(t("Recurring task updated"));
            onClose();
          } else {
            showErrorToast(t("Failed to update recurring task"));
          }
        });
      }
    },
    [dispatch, id, onClose, projectId, setRecurringTaskCreated, t, users]
  );

  const handleKeyPress = useCallback((event) => {
    if (submitButtonRef.current && event.key === keyCodes.enter && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      submitButtonRef.current.click();
    }
  }, []);

  const handleCancel = useCallback(() => {
    if (formRef?.current?.dirty) {
      setShowConfirmationModal(true);
    } else {
      if (onClose) {
        onClose();
      }
    }
  }, [onClose]);

  const handleKeyPressEditor = useCallback((event) => {
    if (event.key === keyCodes.enter && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
    }
  }, []);

  const handleUpload = useCallback(
    (files) => {
      for (let i = 0; i < files.length; i++) {
        onUpload(files[i]);
      }
    },
    [onUpload]
  );

  const taskListsOptions = useMemo(
    () =>
      taskLists
        .toList()
        .toJS()
        .map((label) => ({
          id: label.id,
          name: label.name,
        })),
    [taskLists]
  );

  const handleUpdateSubtasksFromRealTime = useCallback((id) => {
    if (id) {
      const updatedSubtasks = formRef?.current?.values?.subtasks?.map((subtask) => {
        if (subtask.assignee_id === id) {
          return { ...subtask, assignee_id: null };
        }
        return subtask;
      });
      setUpdatedSubtasksFromRealTime(updatedSubtasks);
    }
  }, []);

  useGlobalPrivateChannelListener("UserMovedToTrash", (user) => {
    handleUpdateSubtasksFromRealTime(user.id);
  });
  useGlobalPrivateChannelListener("UserMovedToArchive", (user) => {
    handleUpdateSubtasksFromRealTime(user.id);
  });

  const keyDownOnInput = useCallback(
    (e) => {
      if (e.code === "Enter") {
        e.preventDefault();
        handleSubmit();
        handleCancel();
      }
    },
    [handleCancel, handleSubmit]
  );

  const handleOpenVariableMenu = useCallback(() => {
    setVariableMenuOpen(!variableMenuOpen);
  }, [variableMenuOpen]);
  const handleCloseVariableMenu = useCallback(() => {
    setVariableMenuOpen(false);
  }, []);

  const handleAddVariable = useCallback(
    (name) => {
      const input = inputRef.current;

      if (input) {
        const currentValue = input.value;
        const variableText = name;
        const newPosition = inputCursorPosition + variableText.length;

        const updatedInput =
          currentValue.substring(0, inputCursorPosition) + variableText + currentValue.substring(inputCursorPosition);
        setVariableMenuOpen(false);
        setInputCursorPosition(newPosition);
        setTimeout(() => {
          input.focus();
          input.setSelectionRange(newPosition, newPosition);
        }, 100);
        return updatedInput;
      }
    },
    [inputCursorPosition]
  );

  const onSelectionChange = useCallback((e) => {
    setInputCursorPosition(e.target.selectionStart);
  }, []);

  const handleConfirmModalSubmit = useCallback(() => {
    setShowConfirmationModal(false);
    if (onClose) {
      onClose();
    }
  }, [onClose]);

  const handleCloseConfirmationModal = useCallback(() => {
    setShowConfirmationModal(false);
  }, []);

  const onKeyDownCallback = useCallback(
    (e) => {
      e.stopPropagation();

      if (e.key === "Escape" && formRef?.current?.dirty) {
        setShowConfirmationModal(true);
      }

      if (e.key === "Escape" && !formRef?.current?.dirty) {
        onClose();
      }
    },
    [onClose]
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className="tw-flex tw-max-w-[1000px] tw-w-full"
      disableCloseOnEsc
      onKeyDown={onKeyDownCallback}
      onClickOutside={handleCancel}
    >
      <Formik
        initialValues={{
          repeat_frequency: repeatFrequency,
          repeat_amount: repeatAmount,
          repeat_amount_extended: repeatAmountExtended,
          name,
          description,
          bodyMode,
          task_list_id: taskListId,
          assigneeId,
          isBillable,
          subtasks,
          dates: {
            start_in: startOn,
            due_in: dueOn,
          },
          subscribers: subscribers,
          labels: {
            labelIds,
            labelNames,
          },
          timeEstimate: {
            estimate,
            job_type_id: jobTypeId,
          },
          hiddenFromClients: isLoggedUserClient ? false : hiddenFromClients,
          important,
          attachments: {
            filesToAttach: attachUploadedFiles,
            filesToDrop: dropAttachedFiles,
          },
        }}
        validationSchema={recurringTaskValidator}
        onSubmit={handleSubmit}
        innerRef={formRef}
      >
        {({ values, setFieldValue, setFieldError, setFieldTouched, isSubmitting, errors, handleSubmit }) => {
          const handleFormSubmit = (e) => {
            e.preventDefault();
            handleSubmit();

            if (Object.keys(errors)?.length) {
              return focusFirstInvalidField(errors, true);
            }
          };
          return (
            <Form
              onKeyDown={handleKeyPress}
              onSubmit={handleFormSubmit}
              className="tw-flex tw-overflow-hidden tw-flex-col tw-w-full tw-pr-6 sm:tw-pr-3 lg:tw-pr-0"
              data-testid="task-form"
            >
              <Dialog.Title>{t("Recurring Task Options")}</Dialog.Title>
              <Dialog.ContentDivider />
              <Dialog.Content>
                <div className="tw-flex tw-flex-col lg:tw-flex-row tw-max-h-full tw-flex-1 lg:tw-pr-0">
                  <div className="tw-flex tw-rounded-md tw-w-[588px] tw-shrink-0 tw-h-full tw-bg-page-paper-main tw-flex-col">
                    <RecurrenceSelect />
                    <div className="tw-flex tw-flex-col tw-min-h-[483px]">
                      <Field name="name">
                        {({ form, field, meta }) => (
                          <div className="tw-flex tw-flex-row tw-px-6 tw-py-1.5 tw-w-full tw-border-border-primary tw-border-solid tw-border tw-rounded-t-md tw-border-b-0">
                            <Input
                              ref={(el) => {
                                inputRef.current = el;
                                if (fieldRef) return (fieldRef.current.name = el);
                              }}
                              endAdornment={
                                <>
                                  {meta.error && meta.touched ? (
                                    <Tooltip title={capitalize(meta.error)}>
                                      <InfoIcon
                                        fill="var(--red-alert)"
                                        className="tw-cursor-help tw-mt-0.5 tw-mr-1"
                                      />
                                    </Tooltip>
                                  ) : null}
                                  <InsertTextVariables
                                    handleOpenMenu={handleOpenVariableMenu}
                                    handleCloseMenu={handleCloseVariableMenu}
                                    variableMenuOpen={variableMenuOpen}
                                    type="for-tasks"
                                    handleSelectVariable={(v) => {
                                      setFieldValue("name", handleAddVariable(v));
                                    }}
                                  />
                                </>
                              }
                              id="taskNameInput"
                              value={field.value}
                              size="biggest"
                              mode="flat"
                              tabIndex={0}
                              maxLength={150}
                              placeholder={`${t("Task name")} *`}
                              data-testid="project-recurring-task-name-input"
                              className="tw-w-full tw-p-0"
                              onSelect={onSelectionChange}
                              onKeyDown={keyDownOnInput}
                              onChange={(e) => {
                                form.setFieldTouched(field.name, false);
                                form.setFieldError(field.name, undefined);
                                form.setFieldValue(field.name, e.target.value);
                              }}
                              autoFocus
                            />
                          </div>
                        )}
                      </Field>
                      <div className="tw-border-border-primary tw-border-solid tw-border tw-flex-1 tw-border-t-transparent recurring_task_form_description_container">
                        <CustomFormField name="description">
                          {({ field }) => (
                            <VisualEditorContainer
                              projectId={projectId}
                              parentType="RecurringTask"
                              parentId={id}
                              content={field.value}
                              bodyMode={values?.bodyMode}
                              onKeyDown={handleKeyPressEditor}
                              onChange={field.onChange}
                              placeholder={t(`Write a description or press "/" for commands`)}
                              showTextVariables
                            />
                          )}
                        </CustomFormField>
                      </div>
                      <div className="tw-px-6 tw-py-5 tw-border tw-border-border-primary tw-border-solid tw-overflow-hidden tw-border-t-transparent">
                        <CustomFormField name="attachments">
                          {({ field }) => (
                            <SelectAttachments
                              {...fileHandlerRest}
                              uploading={uploading}
                              attachmentIds={attachments}
                              filesToAttach={field.value.filesToAttach}
                              filesToDrop={field.value.filesToDrop}
                              onChange={field.onChange}
                              onOpenFileWindow={handleOpenFileWindow}
                            />
                          )}
                        </CustomFormField>
                      </div>
                      <div className="tw-px-6 tw-h-[66px] tw-flex tw-items-center tw-border tw-border-border-primary tw-border-solid tw-overflow-hidden tw-border-t-transparent tw-rounded-b-md">
                        <Body2
                          weight="bold"
                          className="tw-truncate tw-w-[100px] tw-mr-1"
                        >
                          {t("Subscribers")}
                        </Body2>
                        <CustomFormField name="subscribers">
                          {({ field }) => {
                            return (
                              <SubscribersPicker
                                onChange={field.onChange}
                                hideClients={values.hiddenFromClients}
                                selected={field.value}
                                userIds={possibleSubscribers}
                                target={
                                  <SubscribersAvatarList
                                    taskId={id}
                                    subscriberIds={field.value}
                                  />
                                }
                              />
                            );
                          }}
                        </CustomFormField>
                      </div>
                    </div>
                    <AngDropFiles onDrop={handleUpload} />
                    <div className="tw-flex tw-flex-col object_subtasks tw-mb-0 tw-border-0 tw-pt-6">
                      <div className="object_subtasks_inner tw-mb-0">
                        {values?.subtasks?.length ? <Header3 className="tw-mb-2">{t("Subtasks")}</Header3> : null}
                        <FieldArray name="subtasks">
                          {({ replace, remove, push }) => {
                            return (
                              <>
                                <RecurringSubtaskList
                                  projectId={projectId}
                                  users={users}
                                  subtasks={values?.subtasks ?? []}
                                  hideFromClients={values?.hiddenFromClients}
                                  onChangeAssigneeOnSubtask={(assigneeId, index) => {
                                    if (updatedSubtasksFromRealTime.length > 0) {
                                      setUpdatedSubtasksFromRealTime([]);
                                    }
                                    replace(index, {
                                      ...values.subtasks[index],
                                      assignee_id: assigneeId ? +assigneeId : null,
                                    });
                                  }}
                                  onUpdateSubtask={(subtask, index) => replace(index, subtask)}
                                  onDeleteSubtask={(i) => remove(i)}
                                  onSubtasksReorder={(reorderedSubtasks) => {
                                    setFieldValue("subtasks", reorderedSubtasks);
                                  }}
                                  resolvedAssignee={resolveSubtaskAssignee}
                                />
                                <div
                                  className="new_subtask"
                                  id="new_subtask_form"
                                >
                                  {addNewSubtaskVisible ? (
                                    <SubtaskRecurringTaskForm
                                      onSubmit={(subtask) => {
                                        push(subtask);
                                        setAddNewSubtaskVisible(false);
                                      }}
                                      handleCancel={() => {
                                        setAddNewSubtaskVisible(false);
                                      }}
                                      projectId={projectId}
                                      hideFromClients={values?.hiddenFromClients}
                                    />
                                  ) : (
                                    <AddSubtaskTriggerContainer
                                      handleOnClick={showAddSubtaskForm}
                                      visible
                                      classname="tw-mb-0"
                                    />
                                  )}
                                </div>
                              </>
                            );
                          }}
                        </FieldArray>
                      </div>
                    </div>
                  </div>
                  <div className="tw-w-[328px] tw-shrink-0 lg:tw-px-6 tw-mt-6 lg:tw-pt-5 lg:tw-ml-6 lg:tw-mt-0">
                    <div className="tw-mb-6">
                      <CustomFormField name="task_list_id">
                        {({ field, meta }) => (
                          <>
                            <Label
                              size="small"
                              className="tw-mb-1 tw-block"
                              invalid={meta.error && meta.touched}
                            >
                              {t("Task List")}
                            </Label>
                            <SelectTaskList
                              projectId={projectId}
                              options={taskListsOptions}
                              selected={field.value}
                              onChange={(value) => {
                                setFieldTouched(field.name, false);
                                setFieldError(field.name, undefined);
                                setFieldValue(field.name, value);
                              }}
                              fieldRef={fieldRef}
                              invalid={meta.error && meta.touched}
                              errorMessage={meta.error}
                            />
                          </>
                        )}
                      </CustomFormField>
                    </div>

                    <div className="tw-mb-6">
                      <Label
                        size="small"
                        className="tw-mb-1 tw-block"
                      >
                        {t("Assignee")}
                      </Label>
                      <CustomFormField name="assigneeId">
                        {({ field }) => (
                          <SelectContainer
                            options={members}
                            selected={field.value}
                            onChange={(v) => field.onChange(v ? v : 0)}
                            placeholder={t("Choose an assignee")}
                            defaultValue={t("No Assignee...")}
                            className="tw-w-full"
                          />
                        )}
                      </CustomFormField>
                    </div>
                    <div className="tw-mb-6">
                      <Label
                        size="small"
                        className="tw-mb-1 tw-block"
                      >
                        {t("Due On")}
                      </Label>
                      <CustomFormField name="dates">
                        {({ field }) => (
                          <StartOnDueOn
                            startOn={field.value.start_in === null ? "" : field.value.start_in}
                            dueOn={field.value.due_in === null ? "" : field.value.due_in}
                            onChange={field.onChange}
                          />
                        )}
                      </CustomFormField>
                    </div>
                    <div className="tw-mb-6">
                      <Label
                        size="small"
                        className="tw-mb-1 tw-block"
                      >
                        {t("Labels")}
                      </Label>
                      <CustomFormField name="labels">
                        {({ field }) => {
                          return (
                            <SelectTaskLabels
                              projectId={projectId}
                              labels={labels}
                              selected={field.value.labelIds}
                              onChange={field.onChange}
                            />
                          );
                        }}
                      </CustomFormField>
                    </div>
                    {canSeeTimeEstimates && (
                      <div className="tw-mb-6">
                        <Caption1
                          weight="bold"
                          className="tw-mb-1 tw-block"
                        >
                          {t("Time Estimation")}
                        </Caption1>
                        <CustomFormField name="timeEstimate">
                          {({ field }) => (
                            <SelectTimeEstimate
                              estimate={field.value.estimate}
                              jobTypeId={field.value.job_type_id}
                              onChange={field.onChange}
                              setTabIndex={0}
                            />
                          )}
                        </CustomFormField>
                      </div>
                    )}
                    {hideFromClients && !isLoggedUserClient ? (
                      <CustomFormField name="hiddenFromClients">
                        {({ field }) => (
                          <FormCheckbox
                            id="task_form_hidden_from_clients"
                            name="hiddenFromClients"
                            checked={field.value}
                            onChange={(val) => {
                              setMembers(() => (val ? users.filter((user) => user.class !== "Client") : users));
                              setFieldValue("hiddenFromClients", val);
                              setFieldValue("subscribers", resolveSubscribers(val, values.subscribers));
                              setFieldValue("subtasks", resolveSubtaskAssignee(val, values.subtasks));
                              setFieldValue("assigneeId", resolveRecurringTaskAssignee(val, values.assigneeId));
                            }}
                            className="form_field tw-my-2"
                            text={t("Hidden from clients")}
                          />
                        )}
                      </CustomFormField>
                    ) : null}
                    <CustomFormField name="important">
                      {({ field }) => (
                        <FormCheckbox
                          id="task_form_high_priority"
                          name="important"
                          checked={field.value}
                          onChange={field.onChange}
                          className="form_field tw-my-2"
                          text={t("High priority")}
                        />
                      )}
                    </CustomFormField>
                    {isTrackingEnabled && !isProjectNonBillable ? (
                      <CustomFormField name="isBillable">
                        {({ field }) => (
                          <FormCheckbox
                            id="task_form_is_billable"
                            name="isBillable"
                            checked={field.value}
                            onChange={field.onChange}
                            className="form_field tw-my-2"
                            text={t("Task is billable")}
                          />
                        )}
                      </CustomFormField>
                    ) : null}
                  </div>
                </div>
              </Dialog.Content>
              <Dialog.ContentDivider />
              <Dialog.Actions>
                <Button
                  type="submit"
                  variant="primary"
                  className="tw-mr-3"
                  disabled={isSubmitting || uploading}
                  tabIndex={0}
                >
                  {id ? t("Save") : t("Add Task")}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleCancel}
                  tabIndex={0}
                >
                  {t("Cancel")}
                </Button>
              </Dialog.Actions>
            </Form>
          );
        }}
      </Formik>
      <LayerContextProvider value={{ zIndex: layers.skyscraper }}>
        <ConfirmDialog
          open={showConfirmationModal}
          dialogTitle={t("Discard changes?")}
          dialogContent={t("All unsaved changes will be lost.")}
          confirmBtnText={t("Discard")}
          cancelBtnText={t("Cancel")}
          onConfirm={handleConfirmModalSubmit}
          onCancel={handleCloseConfirmationModal}
        />
      </LayerContextProvider>
      <AttachmentUpload
        ref={fileInputRef}
        multiple
        onUpload={onUpload}
      />
    </Dialog>
  );
};

RecurringTaskForm.propTypes = {
  open: PropTypes.bool,
  id: PropTypes.number,
  projectId: PropTypes.number.isRequired,
  users: PropTypes.array.isRequired,
  attachments: PropTypes.instanceOf(List),
  taskLists: PropTypes.instanceOf(TaskListMap),
  canSeeTimeEstimates: PropTypes.bool,
  name: PropTypes.string,
  repeatFrequency: PropTypes.oneOf([
    "never",
    "daily",
    "weekly",
    "monthly",
    "bimonthly",
    "quarterly",
    "semiyearly",
    "yearly",
  ]),
  repeatAmount: PropTypes.number,
  repeatAmountExtended: PropTypes.number,
  description: PropTypes.string,
  bodyMode: PropTypes.string,
  taskListId: PropTypes.number,
  assigneeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  startOn: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  dueOn: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  hiddenFromClients: PropTypes.bool,
  important: PropTypes.bool,
  estimate: PropTypes.string,
  jobTypeId: PropTypes.number,
  subscribers: PropTypes.array,
  attachUploadedFiles: PropTypes.arrayOf(PropTypes.string),
  dropAttachedFiles: PropTypes.arrayOf(PropTypes.number),
  labels: PropTypes.instanceOf(LabelMap).isRequired,
  labelNames: PropTypes.instanceOf(List),
  labelIds: PropTypes.instanceOf(List),
  onClose: PropTypes.func,
  hideFromClients: PropTypes.bool,
  isLoggedUserClient: PropTypes.bool,
  subtasks: PropTypes.array,
  isBillable: PropTypes.bool,
  isTrackingEnabled: PropTypes.bool,
  isProjectNonBillable: PropTypes.bool,
  projectMembers: PropTypes.array,
};

RecurringTaskForm.defaultProps = {
  id: 0,
  attachments: List(),
  name: "",
  repeatFrequency: "never",
  repeatAmount: 0,
  repeatAmountExtended: 0,
  description: "",
  jobTypeId: 0,
  subscribers: [],
  labelIds: List(),
  hiddenFromClients: false,
  important: false,
  taskLists: new TaskListMap(),
  onClose: () => null,
  onChange: () => null,
  onLabelsSubmit: () => {},
  canSeeTimeEstimates: false,
  attachUploadedFiles: [],
  dropAttachedFiles: [],
  subtasks: [],
  projectMembers: [],
};

RecurringTaskForm.displayName = "RecurringTaskForm";

export default RecurringTaskForm;
